import { IconDatabaseShare } from '@tabler/icons-react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense, use } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { DashboardSpecRenderer } from '#core/components/dashboard/spec/DashboardSpecRenderer.tsx';
import { LabeledLoader } from '#core/components/layout/LabeledLoader.tsx';
import { NO_POS } from '#core/lib/expr/ast.ts';
import { validateDashboardSpec } from '#core/lib/expr/ops/dashboard/validate.ts';
import { TILE_KIND_LABELS, type TileSpec } from '#core/lib/expr/ops/dashboard/value.ts';
import { useBindingResolver } from '#core/lib/expr/session/BindingScopeContext.ts';
import { EXPR_FAIL_FAST, exprQueryKey } from '#core/lib/expr/session/exprQueryKey.ts';
import { useSession } from '#core/lib/expr/session/SessionContext.ts';
import { useEvalExpr } from '#core/lib/expr/session/useEvalExpr.ts';
import { useMaterialize } from '#core/lib/expr/session/useMaterialize.ts';
import { type DashboardSpec, expectDashboardSpec } from '#core/lib/expr/value.ts';

import { type ChartMarker, collectChartMarkers, hasUnannotatedTimeChart } from './markers.ts';
import { ToolContentBubble } from '#plugin-chat/components/tool-display/ToolContentBubble.tsx';
import type { ToolProps } from '#plugin-chat/components/tools/Tool.ts';
import { ToolsContext } from '#plugin-chat/contexts/ToolsContext.ts';
import { Expr, ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { expressionSchema } from '#plugin-chat/lib/tool/dataset-keys.ts';
import { ToolErrorMessage } from '#plugin-chat/lib/tool/message-components.tsx';
import { Tool } from '#plugin-chat/lib/tool/Tool.tsx';
import { expressionArgsIdentity } from '#plugin-chat/lib/tool/tool-identity.ts';
import type { ToolResult } from '#plugin-chat/lib/transport/types.ts';
import { useThreadScopedEval } from '#plugin-chat/session/useThreadScopedEval.ts';

const ShowDashboardInputSchema = z.object({
  expression: expressionSchema.describe('An s-expression whose top-level form is `(dashboard …)`.'),
});
type ShowDashboardInput = z.infer<typeof ShowDashboardInputSchema>;
type ShowDashboardOutput = ToolResult & {
  readonly tileCount: number;
  readonly tiles: readonly { readonly kind: TileSpec['kind']; readonly span: readonly [number, number] }[];
  /** The labeled chart markers plotted on cartesian tiles — insights cite the x-axis ones by label. */
  readonly markers: readonly ChartMarker[];
};

/**
 * Suspense-eval the dashboard expression to its `DashboardSpec`.
 * Evaluation is cheap + pure (the dashboard op builds tile specs
 * holding unresolved `ColumnRef`s — no CDF traffic). Per-tile data
 * resolution happens later, inside `<DashboardSpecRenderer>`. Keyed by
 * `session.id` + the in-scope binding recipes + expression: the recipes
 * are the transcript-derived cross-turn namespace, so a branch
 * divergence re-keys and re-resolves while siblings at the same
 * position share the cache. `useEvalExpr` resolves `(col …)` against
 * the `<BindingScopeContext>` mounted by the `<Tool>` render boundary.
 */
function useDashboardSpec(expression: string): DashboardSpec {
  const evalExpr = useEvalExpr();
  const session = useSession();
  const bindings = useBindingResolver();
  const { data } = useSuspenseQuery({
    queryKey: exprQueryKey('show-dashboard-spec', session, bindings, expression),
    queryFn: async () => expectDashboardSpec(await evalExpr(expression), 'showDashboard render', NO_POS),
    ...EXPR_FAIL_FAST,
  });
  return data;
}

function DashboardResult({ expression }: { expression: string }) {
  const spec = useDashboardSpec(expression);
  // const tools = use(ToolsContext);
  // // The insight citations for this dashboard live on the showInsights call
  // // that follows it in the thread — derive the cited labels reactively so
  // // the chart emphasizes its markers the moment the insights stream in.
  // const cited = useAuiState(
  //   useShallow((state) =>
  //     citedMarkerLabels(state.thread.messages as readonly CitationScanMessage[], state.message.index, {
  //       dashboard: tools.ShowDashboard,
  //       insights: tools.ShowInsights,
  //     }),
  //   ),
  // );
  // // Hoist the dashboard's tile-export registry one level up so the enclosing
  // // assistant message can aggregate every dashboard's tile snapshot into a
  // // single ZIP — see `useRegisterMessageDashboard`. The registry stays as
  // // the dashboard's own `DashboardExportContext` value via the `registry`
  // // prop on `DashboardSpecRenderer`; no extra plumbing inside the renderer.
  // const registry = useKeyedRegistry<TileExportEntry>();
  // useRegisterMessageDashboard({
  //   id: expression,
  //   name: slugify(spec.title || 'dashboard'),
  //   getEntries: () => registry.entries,
  // });
  return (
    <DashboardSpecRenderer //citedMarkers={new Set(cited)} registry={registry}
      spec={spec}
    />
  );
}
/**
 * Complete-state renderer: the evaluated dashboard inside the standard
 * content bubble. A component (not an inline render expression) so it can
 * consume i18n for the suspense label.
 */
function DashboardContent({ expression }: { expression: string | undefined }) {
  const { t } = useTranslation('chat');
  return (
    <ToolContentBubble>
      <Suspense fallback={<LabeledLoader label={t('tools.showDashboard.loading')} />}>
        {expression && <DashboardResult expression={expression} />}
      </Suspense>
    </ToolContentBubble>
  );
}

/**
 * ShowDashboardTool — s-expr-driven dashboard.
 *
 * The LLM writes an s-expression whose top-level form is
 * `(dashboard :title "…" (metric …) (cartesian …) (table …))`. `execute`
 * evaluates it (eval itself is cheap — tile specs carry unresolved
 * `ColumnRef`s) then `validateDashboardSpec` resolves the
 * scalar-bearing refs and throws on a data-shape violation (e.g. a
 * metric `:value` that resolves to a vector) so the agent learns the
 * dashboard is broken instead of being told it rendered; resolution
 * shares the `session.fetch` cache with `render`, so it is not a
 * second CDF round-trip. On success it returns a tile-summary envelope
 * so the LLM knows the dashboard rendered. `render` re-evaluates (shared Suspense
 * cache) and hands the `DashboardSpec` to the V-shape-native
 * `<DashboardSpecRenderer>`, which materialises each tile's columns on
 * demand with per-tile Suspense + error isolation.
 *
 * No factory props / no `outputDataset` binding — `<ChatSessionProvider>`
 * populates `EvalDepsContext` once for the whole conversation; the
 * render-side hook reads the Session via `useSession`/`useEvalExpr`.
 *
 * The LLM-facing usage guidance (ref-resolution discipline, KPI
 * binding, chart-type selection, the distinct-column rule, reference
 * lines, binary/status handling, long-format pivoting) lives on the
 * `description` prop via a nested `<ToolGuidance>` — `<Tool>` has no
 * separate `instructions` channel. The DSL *grammar* is documented
 * globally in the shared `<ExprDslReference>`; this guidance is
 * strategic/semantic only and is cast against the dashboard s-expr
 * constructs.
 */
export function ShowDashboardTool(props: ToolProps) {
  const scoped = useThreadScopedEval();
  const materialize = useMaterialize();
  const tools = use(ToolsContext);

  return (
    <Tool<ShowDashboardInput, ShowDashboardOutput>
      color="grape"
      description={
        <ToolGuidance
          description="Generate a dashboard (charts, tables, KPIs) to present results to the user."
          whenToUse={[
            'Display results/datasets from other tool calls (`queryKnowledgeGraph`, `transformDataset`)',
            'Display knowledge graph data (table or metric tile)',
            'Display timeseries data (line/area/bar/scatter charts)',
            'Display a single-number answer (a `metric` tile)',
          ]}
        >
          <p>
            <strong>{`Always use ${tools.ShowDashboard} to present results to the user`}</strong> — including data from{' '}
            {tools.QueryKnowledgeGraph} and {tools.TransformDataset}. Never type rows as a markdown/bullet list or
            inline table; that is a hard failure mode. Prose is reserved for the narrative around the dashboard
            (question, insight, next steps), never the rows themselves. When a single value is the answer (e.g. a
            count), render it as a <code>metric</code> tile, not a sentence. A time-axis line/area chart must ship
            annotated: labeled x-axis reference markers at the dated events its signal summaries carry — an unannotated
            time chart is incomplete output.
          </p>
          <p>
            <strong>The widget is the single source of truth for every number.</strong> Once a value lands in a{' '}
            <code>metric</code>/<code>cartesian</code>/<code>table</code> tile, do <strong>not</strong> restate that
            value (KPI, count, rate, latest reading) anywhere in prose — the prose conveys only the <em>insight</em>{' '}
            ("priority oil rate has fallen ~30% since the workover"), never the digits the user can already read off the
            tile. Restating a widget value in text is a hard failure mode.
          </p>
          <p>
            "Show me datapoints", "show me the data for X", "plot/trend X over time" →{' '}
            <strong>
              always render a time-bucketed <code>cartesian</code> dashboard
            </strong>{' '}
            (bucket the raw rows in <code>transformDataset</code> with <code>time-bucket</code> first). Never answer
            such a request with a prose list, a markdown table dump, or a raw-row spill.
          </p>
          <p>
            <strong>A dashboard over an entity cohort always ends with the rows.</strong> Whenever the result set is a
            filtered cohort of entities — "show / list / which X", but equally a count or breakdown computed over such a
            cohort — the last tile is a full-width <code>table</code> of the matching rows themselves (human-readable
            name first, then the columns that drove the filter or analysis); KPIs and breakdown charts sit above it. The
            summary tiles describe the cohort, the closing table <em>is</em> the cohort — users expect to read the
            actual entities at the bottom. Skip it only when the dashboard carries no entity cohort (e.g. a pure
            timeseries trend) or a table tile already lists every matching row.
          </p>

          <h3>Tile structure &amp; layout</h3>
          <p>
            Tiles are positional children rendered left-to-right, top-to-bottom in declaration order on a fixed
            12-column grid. Every tile takes <code>:span [W H]</code> (each 1–12).
          </p>

          <h3>Resolve references before display</h3>
          <p>
            <strong>
              Every column a tile binds ( <code>:value</code>, <code>:y</code>, table <code>:ref</code> ) MUST be a
              resolved scalar or numeric column — NEVER a raw timeseries reference.
            </strong>{' '}
            A <code>ref&lt;timeseries&gt;</code> column holds no values; passing it to a tile renders an opaque id
            string (<code>cdf:ts:…</code>) and the output is broken. Produce the resolved column in{' '}
            <code>transformDataset</code> first (a <code>derive</code> of the latest value, or a{' '}
            <code>time-bucket</code> for a chart series), bind that dataset, then point the tile at it.
          </p>

          <h3>Single-number answers &amp; KPIs</h3>
          <p>
            A <code>metric</code> tile is dataset-driven:{' '}
            <code>(metric :value (col &lt;ds&gt; &lt;col&gt;) :label "…" :format compact)</code>. For a single-number
            answer (e.g. "how many wells in field X"), end the pipeline with a <code>transformDataset</code> aggregate
            producing a 1-row dataset (e.g. <code>(aggregate &lt;wells&gt; :pairs {'{:total_wells (count)}'})</code>)
            and point <code>:value</code> at <code>(col totals total_wells)</code>. For a value-with-trend KPI,
            additionally bind <code>:delta (col &lt;ds&gt; &lt;deltaCol&gt;)</code> — it drives the change/trend
            indicator. When a KPI is the latest value of a timeseries, also resolve its timestamp and bind it via{' '}
            <code>:as-of (col &lt;ds&gt; &lt;tsCol&gt;)</code> so the number is never shown undated.
          </p>

          <h3>Human-readable labels</h3>
          <p>
            Always supply an explicit <code>:label</code> on every <code>metric</code>, <code>series</code>, and table{' '}
            <code>column</code> — e.g. <code>:label "SE0074 Long – Avg Priority Oil Rate"</code>. Raw column identifiers
            are not acceptable user-facing labels.
          </p>

          <p>
            <strong>
              Always label both the X axis and the Y axis (<code>x-axis</code>/<code>y-axis</code> <code>:label</code>
              ). Keep units out of axis and series labels — each value renders with the unit the engine attaches from
              its column schema.
            </strong>
          </p>

          <h3>Chart-type selection</h3>
          <p>
            <code>:type</code> values are in the DSL reference's enum domain. Strategic picks: <code>line</code>/
            <code>area</code> for trends over time; <code>bar</code> for part-to-whole comparisons (≤6 categories);{' '}
            <code>scatter</code> for correlations (add <code>:z (col …)</code> to size points as a bubble chart). There
            is no <code>dot</code> type.
          </p>
          <p>
            <strong>Each series must map to a distinct column.</strong> Two <code>series</code> in one{' '}
            <code>cartesian</code> cannot both bind the same <code>:y (col …)</code> — duplicate column refs collapse
            into a single line and produce a broken render. There is <strong>no series faceting</strong>: to show one
            line per category you must emit an explicit <code>series</code> per column (pivot wide first — see below) or
            use a single category <code>bar</code>. To overlay related metrics, point each series at its own column
            (they may even come from different bound datasets sharing an x column).
          </p>

          <h3>Color and style guidelines</h3>
          <p>
            Default to solid lines for primary timeseries. Use dashed/dotted lines for benchmarks, targets, or
            regulatory thresholds. Use color and styling strategically to group related series or highlight the most
            important one.
          </p>

          <h3>Bar orientation</h3>
          <p>
            For ranked/categorical comparisons with long labels or many categories, set{' '}
            <code>:orientation horizontal</code> on the <code>cartesian</code> tile (default is vertical). Horizontal
            bars read far better than rotated/truncated x-axis labels.
          </p>

          <h3>Multiple series from different datasets</h3>
          <p>
            <strong>One chart is NOT limited to one dataset.</strong> A single <code>cartesian</code> may overlay{' '}
            <code>series</code> bound to <em>different</em> bound datasets — each <code>series</code> resolves its own{' '}
            <code>(col …)</code> independently. The only requirement is that they share a comparable x column. To plot
            production from one query against an operational metric from another:
          </p>
          <p>
            <code>{`(cartesian (series :type line :id prod :label "Oil rate" :x (col prod-daily day) :y (col prod-daily oil_rate)) (series :type line :id press :label "Wellhead pressure" :x (col press-daily day) :y (col press-daily pressure)) :span [12 6])`}</code>
          </p>
          <p>
            Here <code>prod-daily</code> and <code>press-daily</code> are two distinct bound datasets sharing a{' '}
            <code>day</code> x. Do not force a join in <code>transformDataset</code> just to co-plot them.
          </p>

          <h3>Reference lines and areas — every time chart ships annotated</h3>
          <p>
            <strong>Plot markers first, narrate second.</strong> A time-axis line/area chart is incomplete without its
            markers — and most real events are ranges, so the default marker is a <code>reference-area</code> spanning
            the event's date range (anomaly, downtime, dip, ramp, step-change window); reserve a vertical{' '}
            <code>:axis x</code> <code>reference-line</code> for a true single-date event (an intervention, a choke
            change). When a charted series carries an <code>insights</code> block, lift its events directly — the
            insight timestamps are already ISO 8601 and drop straight into the <code>:start</code>/<code>:end</code>/
            <code>x</code> values. If an event is worth an insight, it is worth a marker.
          </p>
          <p>
            <strong>Insight-driven markers are x-axis only.</strong> Never add an <code>:axis y</code> reference-line
            automatically: no zero/axis-baseline line, no computed statistic like a period average. The only admissible
            y line is a static reference value the data context explicitly provides — a known limit, target, or
            regulatory threshold the charted series is compared against.
          </p>
          <p>
            Every marker carries a short <code>:label</code> (2–4 words, e.g. "Choke change") — the follow-up insights
            cite x-axis markers by exact label, and an unlabeled marker cannot be cited. Cap markers at 5 per chart so
            labels stay readable: when more candidates exist, keep the ones the insights would keep — highest deviation
            first, then most recent. Skip markers only when the series is genuinely featureless; then the insights must
            not name dated events either.
          </p>

          <h3>Binary and status data</h3>
          <p>
            For binary/status data (0/1 like online/offline) and setpoints/discrete states, use <code>:curve step</code>{' '}
            so each value holds until the next change instead of being linearly interpolated. Still humanize the values
            upstream in <code>transformDataset</code> (derive a label column mapping 0→"Offline", 1→"Online") and label
            the series and caption with the human terms, not "1 = online, 0 = offline".
          </p>

          <h3>Long-format data: pivot wide before charting or stacking</h3>
          <p>
            If the dataset has <em>one row per (x, group)</em> with a discriminator column identifying the group (rows
            shaped <code>{'{ timestamp, series, rate }'}</code>), it is <strong>long format</strong> and cannot be
            charted directly — every chart series would have to bind the same <code>rate</code> column, violating the
            distinct-column rule. Reshape to <strong>wide format</strong> (one row per x, one column per group) with a{' '}
            <code>transformDataset</code> <code>(pivot …)</code> first (see that tool's wide-shaping guidance), then
            point each <code>series</code> at its own per-group column. Stacking (<code>:stack stacked</code> /{' '}
            <code>:stack percent</code>) has the same requirement and no escape hatch — there is no "stack by category"
            facet, so a stacked chart always needs one wide column and one <code>series</code> per stacked category, all
            sharing the x.
          </p>

          <example name="transform-to-cartesian-dashboard">
            <p>User: "Plot daily revenue vs. cost for 2026."</p>
            <ol>
              <li>
                <code>transformDataset</code> — bucket the raw rows to a daily grain (<code>time-bucket</code>),
                outputDataset <code>daily-fin</code> → binds <code>daily-fin</code>
              </li>
              <li>
                <code>showDashboard</code> — expression{' '}
                <Expr>{`(dashboard :title "Daily revenue vs cost" (cartesian (series :type line :id rev :label "Revenue" :color blue :x (col daily-fin day) :y (col daily-fin revenue)) (series :type line :id cost :label "Cost" :color red :x (col daily-fin day) :y (col daily-fin cost)) (reference-line :axis x :value "2026-03-28" :label "Cost spike" :style dashed) :span [12 6]))`}</Expr>
              </li>
            </ol>
          </example>

          <example name="count-plus-list-layout">
            <p>
              User: "Which wells are flowing?" — a count <em>and</em> the underlying list. Compose a small{' '}
              <code>metric</code> total on top, then a <code>table</code> of the individual rows below; use{' '}
              <code>:span</code> so they stack full-width:
            </p>
            <ol>
              <li>
                <code>transformDataset</code> → <code>flowing-wells</code> (the filtered well rows), and an{' '}
                <code>aggregate (count)</code> → <code>flowing-count</code> (1-row)
              </li>
              <li>
                <code>showDashboard</code> — expression{' '}
                <Expr>{`(dashboard :title "Flowing wells" (metric :value (col flowing-count n) :label "Flowing wells" :format compact :span [12 2]) (table (column :ref (col flowing-wells name) :label "Well" :format text) (column :ref (col flowing-wells field) :label "Field" :format text) (column :ref (col flowing-wells oil_rate) :label "Oil rate" :format number) :span [12 8]))`}</Expr>
              </li>
            </ol>
          </example>
        </ToolGuidance>
      }
      errorResponseProps={() => ({
        flowSignal: {
          appendMessages: [
            {
              role: 'user',
              content: `Review the provided dashboard expression and fix errors as described in the error message above.

Proceed to call ${tools.TransformDataset} to resolve dataset related issues before calling ${tools.ShowDashboard} again with corrected input parameters and/or updated datasets.

If you're unable to recover from the error, abort and inform the user using ${tools.CreateResponse} with "error" kind and a concise plain-language error description.`,
            },
          ],
          narrowTools: [tools.TransformDataset, tools.QueryKnowledgeGraph, tools.ShowDashboard, tools.CreateResponse],
          continuation: 'continue',
        },
      })}
      execute={async (args: ShowDashboardInput): Promise<ShowDashboardOutput> => {
        // Eval is cheap + pure (tile specs carry unresolved ColumnRefs),
        // but a V-shape dashboard can only be told "rendered" once its
        // data shape is checked: a metric :value that resolves to a
        // vector is a misconfigured ref the agent must fix. Resolve +
        // validate the scalar-bearing refs here so the failure is
        // thrown — the <Tool> boundary maps it (an ExprError) to the
        // structured { success:false, … } envelope the LLM recovers
        // from — instead of surfacing only as a silent ! card at render.
        // Resolution shares the session.fetch cache with render's
        // useResolvedColumns, so it is not a second CDF round-trip.
        const { evaluate } = scoped();
        const value = await evaluate(args.expression);
        const spec = expectDashboardSpec(value, 'showDashboard result', NO_POS);
        await validateDashboardSpec(spec, materialize, NO_POS);
        const markers = collectChartMarkers(spec.tiles);
        const needsMarkers = hasUnannotatedTimeChart(spec.tiles);

        return {
          success: true,
          tileCount: spec.tiles.length,
          tiles: spec.tiles.map((t) => ({ kind: t.kind, span: t.span })),
          markers,
          flowSignal: {
            narrowTools: [tools.ShowDashboard, tools.ShowInsights],
            requireTool: true,
            // The forced marker re-run corrects THIS dashboard: the next
            // showDashboard call supersedes it instead of stacking a
            // duplicate in the transcript and UI.
            replacedByNextCall: needsMarkers || undefined,
            appendMessages: [
              {
                role: 'user' as const,
                // TODO: inline prompts
                content: needsMarkers
                  ? `The time chart has no x-axis reference markers. Re-run ${tools.ShowDashboard} with the same expression, adding a short-labeled reference-area over each notable period and an x-axis reference-line only at true single-date events, from the series' signal summaries — at most 5, highest deviation first, then most recent. Only if every charted series is genuinely featureless, call ${tools.ShowInsights} directly.`
                  : `Summarize the insights from the prior transform tool calls, and suggest relevant follow-up questions based on any statistical insights and the dashboard you have rendered. The dashboard plotted these chart markers: ${markers
                      .filter((marker) => marker.axis === 'x')
                      .map((m) => `"${m.label}" (reference ${m.kind})`)
                      .join(
                        ', ',
                      )}. Every insight that describes the dated event or period one of these markers annotates must cite it — set the insight's marker field to the byte-exact label, and never quote or restate the label inside the insight text. Order insights by descending deviation, then most recent first.`,
              },
            ],
          },
        };
      }}
      icon={IconDatabaseShare}
      identityKey={expressionArgsIdentity}
      message={{
        result: ({ output }) => (
          <p>
            The <code>showDashboard</code> expression was accepted and handed to the renderer ({output.tileCount} tile
            {output.tileCount === 1 ? '' : 's'}: {output.tiles.map((t) => TILE_KIND_LABELS[t.kind]).join(', ')}). This
            confirms the data shape is valid — it does <strong>not</strong> confirm the tiles drew correctly, and a
            render-time failure is not visible to you here. If the user reports the dashboard is wrong, empty, or
            broken, re-run <code>showDashboard</code> with a corrected expression — that is the correct response, never
            a redundant repeat, and never tell the user to scroll or that it is already shown above.
          </p>
        ),
        error: ({ envelope, toolName }) => (
          <ToolErrorMessage
            envelope={envelope}
            // recovery={
            //   <p>
            //     The expression must evaluate to <code>(dashboard …)</code>. Ensure every <code>(col …)</code> names a
            //     bound dataset (run its producing tool first), then retry.
            //   </p>
            // }
            toolName={toolName}
          />
        ),
      }}
      parameters={ShowDashboardInputSchema}
      render={{
        complete: ({ args }) => <DashboardContent expression={args.expression} />,
      }}
      revisable
      sticky
      {...props}
    />
  );
}
